import { useState, useMemo, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Pencil, Trash2, Plus, Check, X, TrendingUp, TrendingDown, DollarSign, GripVertical } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

export default function Budget() {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [orderedBudgets, setOrderedBudgets] = useState([]);
  const [newRow, setNewRow] = useState(null);
  const [newIncomeRow, setNewIncomeRow] = useState(null);
  const [editingIncomeId, setEditingIncomeId] = useState(null);
  const [editIncomeValues, setEditIncomeValues] = useState({});

  const { data: budgets = [] } = useQuery({
    queryKey: ['budgets'],
    queryFn: () => base44.entities.Budget.list('category'),
  });

  const { data: budgetOrders = [] } = useQuery({
    queryKey: ['budget_order'],
    queryFn: () => base44.entities.BudgetOrder.list(),
  });

  const budgetOrder = budgetOrders[0] || null;

  const saveBudgetOrderMutation = useMutation({
    mutationFn: (orderedIds) => {
      if (budgetOrder) {
        return base44.entities.BudgetOrder.update(budgetOrder.id, { ordered_ids: orderedIds });
      } else {
        return base44.entities.BudgetOrder.create({ ordered_ids: orderedIds });
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['budget_order'] }),
  });

  useEffect(() => {
    if (budgets.length === 0) { setOrderedBudgets([]); return; }
    const savedIds = budgetOrder?.ordered_ids || [];
    if (savedIds.length > 0) {
      const budgetMap = Object.fromEntries(budgets.map(b => [b.id, b]));
      const ordered = savedIds.filter(id => budgetMap[id]).map(id => budgetMap[id]);
      const remaining = budgets.filter(b => !savedIds.includes(b.id));
      setOrderedBudgets([...ordered, ...remaining]);
    } else {
      setOrderedBudgets(budgets);
    }
  }, [budgets, budgetOrder]);

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(orderedBudgets);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    setOrderedBudgets(reordered);
    saveBudgetOrderMutation.mutate(reordered.map(b => b.id));
  };

  const { data: incomes = [] } = useQuery({
    queryKey: ['monthly_incomes'],
    queryFn: () => base44.entities.MonthlyIncome.list('source'),
  });

  const createIncomeMutation = useMutation({
    mutationFn: (data) => base44.entities.MonthlyIncome.create(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['monthly_incomes'] }); setNewIncomeRow(null); },
  });

  const updateIncomeMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.MonthlyIncome.update(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['monthly_incomes'] }); setEditingIncomeId(null); },
  });

  const deleteIncomeMutation = useMutation({
    mutationFn: (id) => base44.entities.MonthlyIncome.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['monthly_incomes'] }),
  });

  const { data: transactions = [] } = useQuery({
    queryKey: ['transactions'],
    queryFn: async () => {
      const PAGE = 5000;
      let all = [], skip = 0;
      while (true) {
        const page = await base44.entities.Transaction.list('-date', PAGE, skip);
        all = all.concat(page);
        if (page.length < PAGE) break;
        skip += PAGE;
      }
      return all;
    },
  });

  const MORTGAGE_CATEGORIES = new Set([
    'Transfer - ANZ Offset',
    'Transfer - CBA',
    'Transfer - ME Transaction',
  ]);
  const MORTGAGE_LABEL = 'Mortgage Repayments';

  const SHOPPING_CATEGORIES = new Set([
    'Dan Shopping',
    'Sarah Shopping',
    'Online Shopping',
    'Homewares',
  ]);
  const SHOPPING_LABEL = 'Shopping';

  const allCategories = useMemo(() => {
    const cats = [...new Set(transactions.map(t => t.category).filter(Boolean))].sort();
    const budgetedCats = new Set(budgets.map(b => b.category));
    const hasMortgage = cats.some(c => MORTGAGE_CATEGORIES.has(c));
    const hasShopping = cats.some(c => SHOPPING_CATEGORIES.has(c));
    const filtered = cats.filter(c => !budgetedCats.has(c) && !MORTGAGE_CATEGORIES.has(c) && !SHOPPING_CATEGORIES.has(c));
    if (hasMortgage && !budgetedCats.has(MORTGAGE_LABEL)) filtered.push(MORTGAGE_LABEL);
    if (hasShopping && !budgetedCats.has(SHOPPING_LABEL)) filtered.push(SHOPPING_LABEL);
    return filtered.sort();
  }, [transactions, budgets]);

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.Budget.create(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['budgets'] }); setNewRow(null); },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Budget.update(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['budgets'] }); setEditingId(null); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.Budget.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['budgets'] }),
  });

  const startEdit = (budget) => {
    setEditingId(budget.id);
    setEditValues({ amount: budget.amount, period: budget.period });
  };

  const saveEdit = (id) => {
    updateMutation.mutate({ id, data: { amount: parseFloat(editValues.amount), period: editValues.period } });
  };

  const saveNew = () => {
    if (!newRow?.category || !newRow?.amount) return;
    createMutation.mutate({ category: newRow.category, amount: parseFloat(newRow.amount), period: newRow.period || 'monthly' });
  };

  const saveNewIncome = () => {
    if (!newIncomeRow?.source || !newIncomeRow?.amount) return;
    createIncomeMutation.mutate({ source: newIncomeRow.source, amount: parseFloat(newIncomeRow.amount), period: newIncomeRow.period || 'monthly' });
  };

  const saveIncomeEdit = (id) => {
    updateIncomeMutation.mutate({ id, data: { amount: parseFloat(editIncomeValues.amount), source: editIncomeValues.source, period: editIncomeValues.period || 'monthly' } });
  };

  const totalMonthlyIncome = useMemo(() => incomes.reduce((sum, i) => sum + (i.period === 'annual' ? (i.amount || 0) / 12 : (i.amount || 0)), 0), [incomes]);
  const totalMonthlyExpenses = useMemo(() =>
    budgets.reduce((sum, b) => sum + (b.period === 'annual' ? b.amount / 12 : b.amount), 0),
  [budgets]);
  const surplus = totalMonthlyIncome - totalMonthlyExpenses;
  const utilisation = totalMonthlyIncome > 0 ? (totalMonthlyExpenses / totalMonthlyIncome) * 100 : 0;

  return (
    <div className="p-8 max-w-3xl mx-auto space-y-6">
      <div className="mb-2">
        <h1 className="text-xl font-bold text-slate-800">Budget Tracker</h1>
        <p className="text-sm text-slate-400 mt-1">Set monthly or annual spending limits per category</p>
      </div>

      {/* Summary Section */}
      {(totalMonthlyIncome > 0 || totalMonthlyExpenses > 0) && (
        <Card className="border-0 shadow-sm bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Monthly Budget Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="rounded-lg bg-emerald-50 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                  <span className="text-xs text-slate-500 font-medium">Total Budgeted Income</span>
                </div>
                <p className="text-lg font-bold text-emerald-600">${totalMonthlyIncome.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</p>
              </div>
              <div className="rounded-lg bg-rose-50 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingDown className="w-4 h-4 text-rose-500" />
                  <span className="text-xs text-slate-500 font-medium">Total Budgeted Expenses</span>
                </div>
                <p className="text-lg font-bold text-rose-600">${totalMonthlyExpenses.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</p>
                {totalMonthlyIncome > 0 && (
                  <p className="text-xs text-slate-400 mt-0.5">{utilisation.toFixed(1)}% of income</p>
                )}
              </div>
              <div className={`rounded-lg p-4 ${surplus >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className={`w-4 h-4 ${surplus >= 0 ? 'text-blue-500' : 'text-orange-500'}`} />
                  <span className="text-xs text-slate-500 font-medium">{surplus >= 0 ? 'Surplus' : 'Deficit'}</span>
                </div>
                <p className={`text-lg font-bold ${surplus >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                  {surplus >= 0 ? '+' : '-'}${Math.abs(surplus).toLocaleString('en-AU', { minimumFractionDigits: 2 })}
                </p>
                {surplus >= 0 && totalMonthlyIncome > 0 && (
                  <p className="text-xs text-slate-400 mt-0.5">{(100 - utilisation).toFixed(1)}% unallocated</p>
                )}
                {surplus < 0 && totalMonthlyIncome > 0 && (
                  <p className="text-xs text-orange-500 mt-0.5">{utilisation.toFixed(1)}% of income · {(utilisation - 100).toFixed(1)}% overallocated</p>
                )}
                {surplus < 0 && totalMonthlyIncome === 0 && (
                  <p className="text-xs text-orange-500 mt-0.5">No income budgeted</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly Income Section */}
      <Card className="border-0 shadow-sm bg-white">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Monthly Income</CardTitle>
          {!newIncomeRow && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs" onClick={() => setNewIncomeRow({ source: '', amount: '' })}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Income
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-400 uppercase">Source</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-400 uppercase">Period</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-slate-400 uppercase">Amount</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-slate-400 uppercase">Monthly Equiv.</th>
                  <th className="w-20" />
                </tr>
              </thead>
              <tbody>
                {newIncomeRow && (
                  <tr className="border-b border-slate-50 bg-emerald-50/40">
                    <td className="py-2 px-3">
                      <Input
                        value={newIncomeRow.source}
                        onChange={e => setNewIncomeRow(r => ({ ...r, source: e.target.value }))}
                        className="h-8 text-xs w-44"
                        placeholder="e.g. Salary"
                      />
                    </td>
                    <td className="py-2 px-3">
                      <Select value={newIncomeRow.period || 'monthly'} onValueChange={v => setNewIncomeRow(r => ({ ...r, period: v }))}>
                        <SelectTrigger className="h-8 text-xs w-28"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="annual">Annual</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-2 px-3">
                      <Input
                        type="number"
                        value={newIncomeRow.amount}
                        onChange={e => setNewIncomeRow(r => ({ ...r, amount: e.target.value }))}
                        className="h-8 text-xs text-right w-28 ml-auto"
                        placeholder="0.00"
                      />
                    </td>
                    <td className="py-2 px-3 text-right text-slate-400 text-xs">
                      {newIncomeRow.amount ? `$${((newIncomeRow.period === 'annual' ? parseFloat(newIncomeRow.amount) / 12 : parseFloat(newIncomeRow.amount)) || 0).toFixed(2)}` : '—'}
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex justify-end gap-1">
                        <button onClick={saveNewIncome} className="p-1.5 rounded hover:bg-emerald-100 text-emerald-600"><Check className="w-3.5 h-3.5" /></button>
                        <button onClick={() => setNewIncomeRow(null)} className="p-1.5 rounded hover:bg-slate-100 text-slate-400"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                )}
                {incomes.length === 0 && !newIncomeRow && (
                  <tr><td colSpan={5} className="py-8 text-center text-slate-400 text-sm">No income sources added yet.</td></tr>
                )}
                {incomes.map(inc => (
                  <tr key={inc.id} className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="py-2.5 px-3 font-medium text-slate-700">{inc.source}</td>
                    <td className="py-2.5 px-3">
                      {editingIncomeId === inc.id ? (
                        <Select value={editIncomeValues.period || 'monthly'} onValueChange={v => setEditIncomeValues(e => ({ ...e, period: v }))}>
                          <SelectTrigger className="h-8 text-xs w-28"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="annual">Annual</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-xs text-slate-500 capitalize">{inc.period || 'monthly'}</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      {editingIncomeId === inc.id ? (
                        <Input type="number" value={editIncomeValues.amount} onChange={e => setEditIncomeValues(v => ({ ...v, amount: e.target.value }))} className="h-8 text-xs text-right w-28 ml-auto" />
                      ) : (
                        <span className="font-medium text-slate-700">${inc.amount.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-right text-slate-400 text-xs">
                      ${((inc.period === 'annual' ? inc.amount / 12 : inc.amount) || 0).toFixed(2)}/mo
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="flex justify-end gap-1">
                        {editingIncomeId === inc.id ? (
                          <>
                            <button onClick={() => saveIncomeEdit(inc.id)} className="p-1.5 rounded hover:bg-emerald-100 text-emerald-600"><Check className="w-3.5 h-3.5" /></button>
                            <button onClick={() => setEditingIncomeId(null)} className="p-1.5 rounded hover:bg-slate-100 text-slate-400"><X className="w-3.5 h-3.5" /></button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => { setEditingIncomeId(inc.id); setEditIncomeValues({ source: inc.source, amount: inc.amount, period: inc.period || 'monthly' }); }} className="p-1.5 rounded hover:bg-slate-100 text-slate-400"><Pencil className="w-3.5 h-3.5" /></button>
                            <button onClick={() => deleteIncomeMutation.mutate(inc.id)} className="p-1.5 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {incomes.length > 0 && (
                  <tr className="border-t-2 border-slate-200 bg-emerald-50/60">
                    <td className="py-3 px-3 text-xs font-bold text-slate-600 uppercase tracking-wide" colSpan={3}>Total Monthly Income</td>
                    <td className="py-3 px-3 text-right font-bold text-emerald-600">${totalMonthlyIncome.toLocaleString('en-AU', { minimumFractionDigits: 2 })}/mo</td>
                    <td />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Category Budgets Section */}
      <Card className="border-0 shadow-sm bg-white">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Category Budgets</CardTitle>
          {!newRow && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs" onClick={() => setNewRow({ category: '', amount: '', period: 'monthly' })}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Expense Budget
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <DragDropContext onDragEnd={onDragEnd}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-400 uppercase">Category</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-400 uppercase">Period</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-slate-400 uppercase">Amount</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-slate-400 uppercase">Monthly Equiv.</th>
                    <th className="w-20" />
                  </tr>
                </thead>
                <Droppable droppableId="budgets">
                  {(provided) => (
                    <tbody ref={provided.innerRef} {...provided.droppableProps}>
                      {newRow && (
                        <tr className="border-b border-slate-50 bg-emerald-50/40">
                          <td className="py-2 px-3">
                            <Select value={newRow.category} onValueChange={v => setNewRow(r => ({ ...r, category: v }))}>
                              <SelectTrigger className="h-8 text-xs w-44">
                                <SelectValue placeholder="Select category" />
                              </SelectTrigger>
                              <SelectContent>
                                {allCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="py-2 px-3">
                            <Select value={newRow.period} onValueChange={v => setNewRow(r => ({ ...r, period: v }))}>
                              <SelectTrigger className="h-8 text-xs w-28"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="monthly">Monthly</SelectItem>
                                <SelectItem value="annual">Annual</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="py-2 px-3">
                            <Input type="number" value={newRow.amount} onChange={e => setNewRow(r => ({ ...r, amount: e.target.value }))} className="h-8 text-xs text-right w-28 ml-auto" placeholder="0.00" />
                          </td>
                          <td className="py-2 px-3 text-right text-slate-400 text-xs">
                            {newRow.amount ? `$${(newRow.period === 'annual' ? parseFloat(newRow.amount) / 12 : parseFloat(newRow.amount)).toFixed(2)}` : '—'}
                          </td>
                          <td className="py-2 px-3">
                            <div className="flex justify-end gap-1">
                              <button onClick={saveNew} className="p-1.5 rounded hover:bg-emerald-100 text-emerald-600"><Check className="w-3.5 h-3.5" /></button>
                              <button onClick={() => setNewRow(null)} className="p-1.5 rounded hover:bg-slate-100 text-slate-400"><X className="w-3.5 h-3.5" /></button>
                            </div>
                          </td>
                        </tr>
                      )}
                      {orderedBudgets.length === 0 && !newRow && (
                        <tr>
                          <td colSpan={5} className="py-10 text-center text-slate-400 text-sm">No budgets set yet. Click "Add Expense Budget" to get started.</td>
                        </tr>
                      )}
                      {orderedBudgets.map((b, index) => (
                        <Draggable key={b.id} draggableId={b.id} index={index}>
                          {(provided, snapshot) => (
                            <tr
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`border-b border-slate-50 ${snapshot.isDragging ? 'bg-slate-50 shadow-md' : 'hover:bg-slate-50/60'}`}
                            >
                              <td className="py-2.5 px-3 font-medium text-slate-700">
                                <div className="flex items-center gap-2">
                                  <span {...provided.dragHandleProps} className="text-slate-300 hover:text-slate-400 cursor-grab active:cursor-grabbing">
                                    <GripVertical className="w-4 h-4" />
                                  </span>
                                  {b.category}
                                </div>
                              </td>
                              <td className="py-2.5 px-3">
                                {editingId === b.id ? (
                                  <Select value={editValues.period} onValueChange={v => setEditValues(e => ({ ...e, period: v }))}>
                                    <SelectTrigger className="h-8 text-xs w-28"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="monthly">Monthly</SelectItem>
                                      <SelectItem value="annual">Annual</SelectItem>
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <span className="text-xs text-slate-500 capitalize">{b.period}</span>
                                )}
                              </td>
                              <td className="py-2.5 px-3 text-right">
                                {editingId === b.id ? (
                                  <Input type="number" value={editValues.amount} onChange={e => setEditValues(v => ({ ...v, amount: e.target.value }))} className="h-8 text-xs text-right w-28 ml-auto" />
                                ) : (
                                  <span className="font-medium text-slate-700">${b.amount.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</span>
                                )}
                              </td>
                              <td className="py-2.5 px-3 text-right text-slate-400 text-xs">
                                ${(b.period === 'annual' ? b.amount / 12 : b.amount).toFixed(2)}/mo
                              </td>
                              <td className="py-2.5 px-3">
                                <div className="flex justify-end gap-1">
                                  {editingId === b.id ? (
                                    <>
                                      <button onClick={() => saveEdit(b.id)} className="p-1.5 rounded hover:bg-emerald-100 text-emerald-600"><Check className="w-3.5 h-3.5" /></button>
                                      <button onClick={() => setEditingId(null)} className="p-1.5 rounded hover:bg-slate-100 text-slate-400"><X className="w-3.5 h-3.5" /></button>
                                    </>
                                  ) : (
                                    <>
                                      <button onClick={() => startEdit(b)} className="p-1.5 rounded hover:bg-slate-100 text-slate-400"><Pencil className="w-3.5 h-3.5" /></button>
                                      <button onClick={() => deleteMutation.mutate(b.id)} className="p-1.5 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      {orderedBudgets.length > 0 && (
                        <tr className="border-t-2 border-slate-200 bg-rose-50/60">
                          <td className="py-3 px-3 text-xs font-bold text-slate-600 uppercase tracking-wide" colSpan={3}>Total Monthly Expenses</td>
                          <td className="py-3 px-3 text-right font-bold text-rose-600">${totalMonthlyExpenses.toLocaleString('en-AU', { minimumFractionDigits: 2 })}/mo</td>
                          <td />
                        </tr>
                      )}
                    </tbody>
                  )}
                </Droppable>
              </table>
            </DragDropContext>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}